import React, { useState, useEffect } from 'react';
import { Box, TextField, InputAdornment, Popover, Typography, MenuItem } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useListContext } from 'react-admin';
import { API, graphqlOperation } from 'aws-amplify';
import { useTheme } from '@mui/material/styles';

// GraphQL query to fetch sites
const listSites = /* GraphQL */ `
  query ListSites {
    listSites {
      items {
        id
        name
      }
    }
  }
`;

// Helper function to format date as YYYY-MM-DD (local time)
const formatLocalDate = (date) => {
  const localDate = new Date(date);
  localDate.setHours(0, 0, 0, 0);
  return localDate.toLocaleDateString('en-CA'); // Use the ISO format (YYYY-MM-DD)
};

const FilterBar = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const theme = useTheme();

  // State for site selection
  const [sites, setSites] = useState([]);
  const [siteValue, setSiteValue] = useState(filterValues.site || ''); // Save site name

  // State for date range selection
  const initialStart = filterValues.date_gte ? new Date(filterValues.date_gte) : new Date();
  const initialEnd = filterValues.date_lte ? new Date(filterValues.date_lte) : new Date();
  const [range, setRange] = useState([
    { startDate: initialStart, endDate: initialEnd, key: 'selection' }
  ]);
  const [clearKey, setClearKey] = useState(0);  // Added state for forcing a reset of DateRange

  // States for Popover (for site and date range)
  const [anchorDateEl, setAnchorDateEl] = useState(null);
  const [anchorSiteEl, setAnchorSiteEl] = useState(null);
  const dateOpen = Boolean(anchorDateEl);
  const siteOpen = Boolean(anchorSiteEl);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await API.graphql(graphqlOperation(listSites));
        setSites(response.data.listSites.items);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSites([]);
      }
    };
    fetchSites();
  }, []);

  // Handle date range selection
  const handleSelect = (ranges) => {
    const { selection } = ranges;
    setRange([selection]);

    const start = selection.startDate;
    const end = selection.endDate;

    const newFilters = {
      ...filterValues,
      date_gte: start ? start.toISOString() : undefined,
      date_lte: end ? end.toISOString() : undefined,
    };

    if (!start) delete newFilters.date_gte;
    if (!end) delete newFilters.date_lte;

    setFilters(newFilters, displayedFilters); // Apply the filter to the URL params
  };

  // Build the string displayed in the date range input
  const displayLabel = () => {
    const start = filterValues.date_gte ? new Date(filterValues.date_gte) : null;
    const end = filterValues.date_lte ? new Date(filterValues.date_lte) : null;

    if (!start && !end) return '';

    return `${formatLocalDate(start)} ~ ${formatLocalDate(end)}`;
  };

  // Clear date filters and reset Popover state
  const clearDateFilter = () => {
    const newFilters = { ...filterValues };

    delete newFilters.date_gte;
    delete newFilters.date_lte;

    const today = new Date();

    // Reset the range to null or empty date range and increment clearKey to force reset
    setRange([{ startDate: today, endDate: today, key: 'selection' }]);
    setClearKey(prevKey => prevKey + 1);  // Increment clearKey to trigger a re-render

    setFilters(newFilters, displayedFilters);
  };

  // Open the date range popover
  const handleDateOpen = (event) => {
    setAnchorDateEl(event.currentTarget);
  };

  // Close the date range popover
  const handleDateClose = () => {
    setAnchorDateEl(null);
  };

  // Open the site popover
  const handleSiteOpen = (event) => {
    setAnchorSiteEl(event.currentTarget);
  };

  // Close the site popover
  const handleSiteClose = () => {
    setAnchorSiteEl(null);
  };

  // Handle site selection from the Popover
  const handleSiteSelect = (siteName) => {
    setSiteValue(siteName); // Set the selected site value
    setAnchorSiteEl(null); // Close the Popover
    // Apply the filter
    const newFilters = {
      ...filterValues,
      site: siteName || undefined,
    };
    setFilters(newFilters, displayedFilters); // Apply the selected site filter to URL params
  };

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {/* Site filter popover */}
      <TextField
        label="Select Site"
        variant="outlined"
        size="small"
        value={siteValue}
        placeholder="Select site"
        onClick={handleSiteOpen} // Open popover when clicked
        sx={{ minWidth: 200 }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={siteOpen}
        anchorEl={anchorSiteEl}
        onClose={handleSiteClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ minWidth: 200, maxHeight: 500, overflowY: 'auto', padding: '10px' }}>
          <MenuItem value="" onClick={() => handleSiteSelect('')}>All Sites</MenuItem>
          {sites.map((site) => (
            <MenuItem key={site.id} onClick={() => handleSiteSelect(site.name)}>
              {site.name}
            </MenuItem>
          ))}
        </Box>
      </Popover>

      {/* Date range input */}
      <TextField
        label="Date range"
        variant="outlined"
        size="small"
        value={displayLabel()}
        placeholder="Select date range"
        onClick={handleDateOpen} // Open popover when clicked
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarTodayIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 260, cursor: 'pointer' }}
      />

      {/* Date range popover */}
      <Popover
        open={dateOpen}
        anchorEl={anchorDateEl}
        onClose={handleDateClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            p: 1,
            '& .rdrDateRangeWrapper': {
              backgroundColor: '#414354',
            },
            '& .rdrMonths': {
              backgroundColor: '#414354',
            },
            '& .rdrMonthAndYearWrapper': {
              backgroundColor: '#414354',
              color: theme.palette.text.primary,
            },
            '& .rdrWeekDays': {
              backgroundColor: '#414354',
            },
            '& .rdrDateDisplayWrapper': {
              backgroundColor: '#414354',
            },
            '& .rdrDateDisplay': {
              backgroundColor: '#414354',
            },
            '& .rdrDateDisplayItem': {
              backgroundColor: '#414354',
              borderColor: theme.palette.divider,
            },
            '& .rdrDateDisplayItem input': {
              backgroundColor: '#41435400',
              color: theme.palette.text.primary,
            },
            '& .rdrMonthAndYearPickers select': {
              backgroundColor: '#414354',
              color: theme.palette.text.primary,
            },
            '& .rdrNextPrevButton': {
              backgroundColor: '#fff',
              color: theme.palette.text.primary,
            },
            '& .rdrDayNumber span': {
              color: theme.palette.text.primary,
            },
          }}
        >
          <DateRange
            key={clearKey}  // Use the clearKey as the key to force re-render
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            ranges={range}
            months={2}
            direction="horizontal"
          />
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer', ml: 2 }}
              onClick={clearDateFilter} // Call clearDateFilter when clicked
            >
              Clear
            </Typography>
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer', mr: 2 }}
              onClick={handleDateClose}
            >
              Close
            </Typography>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default FilterBar;
export { FilterBar };
